/*
  Pure helpers pulled out of Round.jsx to keep the page component focused
  on wiring, hooks, and JSX. Everything here is a function + no React —
  can be unit-tested in isolation.
*/

/** WHS Course Handicap. Convert a Handicap Index to a Course Handicap:
 *   Course Handicap = round(Index × (Slope / 113) + (Course Rating − Par))
 * Falls back to raw index rounded when rating/slope/par are missing.
 * Returns null if index is missing/blank.
 */
export function courseHandicap(handicapIndex, courseRating, slopeRating, parTotal) {
  if (handicapIndex == null || handicapIndex === "") return null;
  const hi = Number(handicapIndex);
  if (!Number.isFinite(hi)) return null;
  const cr = Number(courseRating);
  const slope = Number(slopeRating);
  const par = Number(parTotal);
  if (!Number.isFinite(cr) || !Number.isFinite(slope) || !Number.isFinite(par) || slope <= 0) {
    return Math.max(0, Math.round(hi));
  }
  return Math.max(0, Math.round(hi * (slope / 113) + (cr - par)));
}

/** WHS basic per-hole strokes received. Pass the Course Handicap (from
 * `courseHandicap` above) so the count reflects proper WHS conversion.
 * base = floor(hcp/18), plus 1 extra if stroke_index ≤ (hcp % 18).
 * Returns 0 when handicap is missing/invalid.
 */
export function whsStrokesReceived(handicap, strokeIndex) {
  if (handicap == null || handicap === "") return 0;
  const hcp = Math.max(0, Math.round(Number(handicap) || 0));
  if (hcp <= 0) return 0;
  const base = Math.floor(hcp / 18);
  const si = Number(strokeIndex);
  const extra = Number.isFinite(si) && si >= 1 && si <= (hcp % 18) ? 1 : 0;
  return base + extra;
}

export function scoreLabel(strokes, par) {
  const diff = strokes - par;
  if (diff <= -3) return "Albatross!";
  if (diff === -2) return "Eagle!";
  if (diff === -1) return "Birdie";
  if (diff === 0) return "Par";
  if (diff === 1) return "Bogey";
  if (diff === 2) return "Double bogey";
  return `+${diff}`;
}

export function haversineYards(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const p1 = (lat1 * Math.PI) / 180;
  const p2 = (lat2 * Math.PI) / 180;
  const dp = ((lat2 - lat1) * Math.PI) / 180;
  const dl = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c * 1.09361;
}

/**
 * Local optimistic mutation of the round doc when a score is saved.
 * Mirrors the server's aggregation logic so the UI paints instantly and
 * the offline queue drains without visible re-renders.
 */
export function optimisticUpdate(round, course, body) {
  if (!round || !course) return round;
  const parByHole = Object.fromEntries(course.holes.map((h) => [h.number, h.par]));
  const applyToScores = (scores) => {
    const kept = scores.filter((s) => s.hole_number !== body.hole_number);
    kept.push({
      hole_number: body.hole_number,
      strokes: body.strokes,
      putts: body.putts ?? null,
    });
    kept.sort((a, b) => a.hole_number - b.hole_number);
    const ts = kept.reduce((sum, s) => sum + s.strokes, 0);
    const played = new Set(kept.map((s) => s.hole_number));
    const tp = Object.entries(parByHole).reduce(
      (sum, [num, par]) => sum + (played.has(Number(num)) ? par : 0),
      0,
    );
    return { scores: kept, total_strokes: ts, total_par: tp };
  };

  if (Array.isArray(round.players) && round.players.length > 1) {
    const idx = body.player_index ?? 0;
    const players = round.players.map((p) => {
      if (p.player_index !== idx) return p;
      const next = applyToScores(p.scores || []);
      return { ...p, ...next };
    });
    const next = { ...round, players };
    if (idx === 0) {
      const host = players[0];
      next.scores = host.scores;
      next.total_strokes = host.total_strokes;
      next.total_par = host.total_par;
    }
    return next;
  }
  const next = applyToScores(round.scores || []);
  return { ...round, ...next };
}
